import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles, FileText, Globe2, Zap, ShieldCheck, Users, Check } from "lucide-react";
import { useAuth } from "@/lib/auth";

export default function Landing() {
  const { user } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-[#FCFCFC]">
      {/* NAV */}
      <header className="border-b border-border bg-white/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="brand-logo">
            <img
              src="/brand/logo.png"
              alt="Traveloble"
              className="h-24 w-auto select-none"
              draggable="false"
            />
            <div className="overline text-[8px] tracking-[0.3em] hidden sm:block border-l border-slate-300 pl-3">QUOTATION SUITE</div>
          </Link>
          <nav className="flex items-center gap-6 text-sm">
            <a href="#features" className="text-slate-600 hover:text-[#16A34A] transition-colors">Features</a>
            <a href="#pricing" className="text-slate-600 hover:text-[#16A34A] transition-colors">Pricing</a>
            {user ? (
              <button
                onClick={() => nav(user.role === "admin" ? "/admin" : "/dashboard")}
                data-testid="nav-open-app-btn"
                className="btn-bronze text-sm"
              >
                Open {user.role === "admin" ? "Admin" : "App"}
              </button>
            ) : (
              <>
                <Link to="/login?admin=1" data-testid="nav-admin-link" className="text-slate-700 hover:text-[#16A34A] flex items-center gap-1">
                  <span className="w-1.5 h-1.5 bg-[#16A34A] rounded-full"></span>
                  Admin
                </Link>
                <Link to="/login" data-testid="nav-login-link" className="text-slate-700 hover:text-[#16A34A]">Log in</Link>
                <Link to="/register" data-testid="nav-register-btn" className="btn-bronze text-sm">Start Free Trial</Link>
              </>
            )}
          </nav>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-24 grid lg:grid-cols-[1.15fr_1fr] gap-14 items-center">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <div className="overline mb-5">Built for premium travel agencies</div>
          <h1 className="font-editorial text-5xl lg:text-6xl leading-[1.02] tracking-tight text-slate-900">
            Craft quotations<br />
            <span className="italic text-[#16A34A]">clients say yes to.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
            A concierge-grade quotation builder for travel agents. Auto-fill agency
            branding, AI-generated destination stories, and magazine-quality PDFs — all
            in under five minutes per quote.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-4">
            <Link to="/register" data-testid="hero-cta-btn" className="btn-bronze inline-flex items-center gap-2">
              Start Your Free Trial <ArrowRight size={16} />
            </Link>
            <Link to="/login" data-testid="hero-login-link" className="text-sm text-slate-700 underline underline-offset-4 decoration-slate-300 hover:text-[#16A34A]">
              Sign in to your account
            </Link>
          </div>

          <div className="mt-14 grid grid-cols-3 gap-8 max-w-lg">
            {[
              { k: "12", l: "Wizard steps" },
              { k: "∞", l: "Quotations/month" },
              { k: "4.9", l: "Agent rating" },
            ].map((s) => (
              <div key={s.l}>
                <div className="font-editorial text-3xl text-slate-900">{s.k}</div>
                <div className="text-xs text-slate-500 mt-1 tracking-wide uppercase">{s.l}</div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.15 }}
          className="relative"
        >
          <div className="paper rounded-lg overflow-hidden">
            <img
              alt="Luxury resort"
              src="https://images.pexels.com/photos/20898616/pexels-photo-20898616.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              className="w-full h-[560px] object-cover"
            />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-white border border-border rounded shadow-xl px-5 py-4 max-w-[220px]">
            <div className="overline text-[9px]">Live PDF Preview</div>
            <div className="font-editorial text-lg mt-1">Santorini Escape</div>
            <div className="text-xs text-slate-500 mt-0.5">7 Nights / 8 Days</div>
            <div className="mt-3 flex items-center gap-1 text-xs text-emerald-700">
              <Check size={13} /> Client-ready
            </div>
          </div>
        </motion.div>
      </section>

      {/* FEATURES */}
      <section id="features" className="bg-white border-t border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
          <div className="overline mb-3">The Suite</div>
          <h2 className="font-editorial text-4xl text-slate-900 max-w-2xl leading-tight">
            Everything a travel agency needs to sell inspiring journeys.
          </h2>

          <div className="mt-14 grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Sparkles, title: "AI-generated content",
                body: "Destination overviews and day-wise itineraries drafted in seconds. Fully editable before you send.",
              },
              {
                icon: FileText, title: "Editorial PDF layouts",
                body: "Sora typography, cover hero images, structured itinerary blocks. Feels like a magazine, prints like a brochure.",
              },
              {
                icon: Globe2, title: "20+ destination catalog",
                body: "Curated countries with flags, cities, and hero imagery. Autocomplete after three letters.",
              },
              {
                icon: Zap, title: "12-step guided builder",
                body: "Progressive wizard so nothing gets missed — from lead passenger to pricing to policies.",
              },
              {
                icon: ShieldCheck, title: "Own your branding",
                body: "Upload your logo, bank details, policies once. Auto-injected into every quote you generate.",
              },
              {
                icon: Users, title: "Admin console",
                body: "Approve agents, monitor revenue, and manage subscriptions from a dedicated admin panel.",
              },
            ].map((f) => (
              <div key={f.title} className="border border-border rounded p-6 hover:border-[#16A34A] transition-colors">
                <f.icon size={22} className="text-[#16A34A]" strokeWidth={1.6} />
                <div className="mt-4 font-editorial text-xl text-slate-900">{f.title}</div>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="bg-[#FCFCFC] border-t border-border">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-24">
          <div className="text-center">
            <div className="overline">Simple pricing</div>
            <h2 className="font-editorial text-4xl mt-3 text-slate-900">One-time annual investment. Unlimited quotations.</h2>
          </div>
          <div className="mt-14 grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {[
              { name: "Starter Annual", price: "₹4,999", desc: "For independent travel consultants", perks: ["Unlimited quotations", "Editorial PDF exports", "AI destination generator", "Email support"] },
              { name: "Pro Annual", price: "₹9,999", desc: "For growing travel agencies", perks: ["Everything in Starter", "Priority phone support", "Custom brand kit", "Team seats (coming soon)"] },
            ].map((p, i) => (
              <div key={p.name} className={`rounded p-7 ${i === 1 ? "bg-[#0F1E5B] text-white" : "bg-white border border-border"}`}>
                <div className={`overline ${i === 1 ? "!text-[#BBF7D0]" : ""}`}>{p.name}</div>
                <div className="font-editorial text-4xl mt-3">{p.price}<span className={`text-sm font-body ml-1 ${i===1?"text-white/60":"text-slate-500"}`}>/year</span></div>
                <div className={`text-sm mt-1 ${i===1?"text-white/70":"text-slate-500"}`}>{p.desc}</div>
                <ul className={`mt-6 space-y-2.5 text-sm ${i===1?"text-white/90":"text-slate-700"}`}>
                  {p.perks.map((x) => (<li key={x} className="flex items-start gap-2"><Check size={14} className={i===1?"text-[#BBF7D0] mt-0.5":"text-[#16A34A] mt-0.5"} /> {x}</li>))}
                </ul>
                <Link to="/register" data-testid={`plan-cta-${i}`} className={`mt-7 block text-center text-sm py-2.5 rounded ${i===1 ? "bg-[#16A34A] hover:bg-[#15803D] text-white transition-colors" : "border border-[#16A34A] text-[#16A34A] hover:bg-[#16A34A] hover:text-white transition-colors"}`}>
                  Get started
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <footer className="bg-[#0F1E5B] text-white/70 text-sm py-10 noise-bg">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="bg-white rounded px-4 py-3">
            <img
              src="/brand/logo.png"
              alt="Traveloble"
              className="h-16 w-auto select-none"
              draggable="false"
            />
          </div>
          <div className="text-xs">© 2026 Traveloble. Crafted for travel agents who care about the details.</div>
        </div>
      </footer>
    </div>
  );
}
